// The picture of a number line: a line with a tick at every step, a number
// under every `labelEvery`-th tick, and lettered points sitting on it.
// Plain React SVG, like practice-graphs.tsx.
//
// The same drawing is the answer control for "tap a point on the number
// line" questions (PracticeTask.tsx): given `onChoose`, every tick also gets
// a real button over it, so the line a student taps can never drift from the
// line a teacher sees.

import { useRef, type KeyboardEvent } from 'react';
import {
  describeNumberLine,
  numberLineDecimal,
  numberLineMarkSpoken,
  numberLineTickSpoken,
  numberLineTickCount,
  type NumberLineSpec,
} from './practice-number-line-data';

const INK = '#2d4a3e';
const CHOSEN = '#1d4637';

// Measured in the drawing's own units, which are one pixel each when the
// picture is shown at its natural width.
const BASE_WIDTH = 300;
const SIDE = 22;
// Ticks are never drawn closer together than this. A line that is tapped
// needs more room for a finger.
const TICK_GAP = 6;
const TAP_GAP = 12;
const LABEL_GAP = 5;
const LETTER_WIDTH = 12;
// Year 8 inequalities: the thick line, the dots at its ends and the arrow's
// head. Two dots of a segment are never closer than DOT_ROOM.
const THICK = 4.5;
const DOT_RADIUS = 5.5;
const RING_WIDTH = 2.5;
const DOT_ROOM = 16;
const HEAD_LENGTH = 12;
const HEAD_HALF = 6.5;
// An arrow's tip reaches past the end of the plain line, so its head never
// sits on the end tick.
const ARROW_REACH = 16;
// The picture may shrink this far to fit a narrow screen, and no further:
// below that it scrolls sideways inside its own box, as a wide table does.
const SMALLEST = 0.85;
const LARGEST = 448 / BASE_WIDTH;

type TickLabel = { whole: string } | { top: string; bottom: string };

// The number under a tick. On a fraction line a whole number is written as
// a whole number, and everything else on the line's own bottom number.
function labelAt(line: NumberLineSpec, at: number): TickLabel {
  const sign = at < 0 ? '−' : '';
  if (!line.fraction) {
    return { whole: sign + numberLineDecimal(line, Math.abs(at)) };
  }
  if (at % line.parts === 0) {
    return { whole: sign + String(Math.abs(at) / line.parts) };
  }
  return { top: sign + String(Math.abs(at)), bottom: String(line.parts) };
}

// Roughly how wide a label is drawn, to keep neighbours from touching.
function labelWidth(label: TickLabel): number {
  if ('whole' in label) {
    // A decimal point takes half the room of a digit or a minus sign.
    const points = label.whole.includes('.') ? 1 : 0;
    return (label.whole.length - points) * 7 + points * 3.5;
  }
  return Math.max(label.top.length, label.bottom.length) * 6.5 + 4;
}

// Roughly how wide a point's name is drawn: its letters are bold and larger
// than the numbers under the ticks.
function markWidth(label: string): number {
  return label.length * 8;
}

export function NumberLineView({
  line,
  chosen = null,
  disabled = false,
  onChoose,
}: {
  line: NumberLineSpec;
  // The tapped tick, counted in the line's parts.
  chosen?: number | null;
  disabled?: boolean;
  // Given only where the line is the answer control. Null clears the tick.
  onChoose?: ((at: number | null) => void) | undefined;
}) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const gaps = numberLineTickCount(line) - 1;
  const ticks = Array.from({ length: gaps + 1 }, (_, index) => {
    const at = line.min + index * line.step;
    const numbered = index % line.labelEvery === 0;
    const mark = line.marks.find((each) => each.at === at) ?? null;
    const label = numbered ? labelAt(line, at) : null;
    return {
      index,
      at,
      numbered,
      // A point that already shows this number above the line does not
      // print it a second time below (Year 2 image check, 23 Oct 2026).
      label:
        label && 'whole' in label && mark?.label === label.whole ? null : label,
      mark,
    };
  });
  const labelled = ticks.filter((tick) => tick.label !== null);
  const marks = [...line.marks].sort((a, b) => a.at - b.at);

  // The smallest gap between ticks at which no two numbers, and no two
  // letters, touch.
  let gap = onChoose ? TAP_GAP : TICK_GAP;
  labelled.forEach((tick, position) => {
    const next = labelled[position + 1];
    if (!tick.label || !next?.label) return;
    const room =
      (labelWidth(tick.label) + labelWidth(next.label)) / 2 + LABEL_GAP;
    gap = Math.max(gap, room / (next.index - tick.index));
  });
  // Points are lettered, or numbered as hops along the line, so a pair of
  // them needs as much room as their two names.
  marks.forEach((mark, position) => {
    const next = marks[position + 1];
    if (next) {
      const room = Math.max(
        LETTER_WIDTH,
        (markWidth(mark.label) + markWidth(next.label)) / 2 + LABEL_GAP,
      );
      gap = Math.max(gap, (room * line.step) / (next.at - mark.at));
    }
  });
  // The two dots of a segment never touch.
  if (line.segment) {
    gap = Math.max(
      gap,
      (DOT_ROOM * line.step) / (line.segment.to.at - line.segment.from.at),
    );
  }
  const span = Math.max(BASE_WIDTH - 2 * SIDE, Math.ceil(gap * gaps));
  const width = span + 2 * SIDE;
  // One division, of whole numbers, for each place.
  const xFor = (at: number) =>
    SIDE + ((at - line.min) * span) / (line.max - line.min);

  const lineY = marks.length > 0 ? 36 : 24;
  // Stacked fractions need more room below the line than plain numbers.
  const stacked = labelled.some((tick) => tick.label && 'top' in tick.label);
  const height = lineY + (stacked ? 46 : 32);
  const description = describeNumberLine(line);

  // Year 8: the thick part of an inequality, drawn over the line and under
  // its dots. A ray runs from its dot to an arrow head past the end tick.
  let thick: { x1: number; x2: number } | null = null;
  let head: string | null = null;
  if (line.segment) {
    thick = { x1: xFor(line.segment.from.at), x2: xFor(line.segment.to.at) };
  } else if (line.ray) {
    const toward = line.ray.arrow === 'left' ? -1 : 1;
    const tip =
      line.ray.arrow === 'left'
        ? SIDE - ARROW_REACH
        : width - SIDE + ARROW_REACH;
    const base = tip - toward * HEAD_LENGTH;
    thick = { x1: xFor(line.ray.at), x2: base };
    head = `${String(tip)},${String(lineY)} ${String(base)},${String(lineY - HEAD_HALF)} ${String(base)},${String(lineY + HEAD_HALF)}`;
  }
  const dots = line.ray
    ? [line.ray]
    : line.segment
      ? [line.segment.from, line.segment.to]
      : [];

  const drawing = (
    <svg
      role={onChoose ? undefined : 'img'}
      aria-label={onChoose ? undefined : description}
      aria-hidden={onChoose ? true : undefined}
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      className="block h-auto w-full"
    >
      <line
        x1={SIDE - 10}
        y1={lineY}
        x2={width - SIDE + 10}
        y2={lineY}
        stroke={INK}
        strokeWidth="2"
      />
      {ticks.map((tick) => {
        const x = xFor(tick.at);
        const reach = tick.numbered ? 8 : 5;
        return (
          <g key={tick.index} data-tick={tick.index}>
            <line
              x1={x}
              y1={lineY - reach}
              x2={x}
              y2={lineY + reach}
              stroke={INK}
              strokeWidth={tick.numbered ? 2 : 1.5}
            />
            {tick.label && 'whole' in tick.label && (
              <text
                x={x}
                y={lineY + (stacked ? 31 : 25)}
                textAnchor="middle"
                className="fill-[#2d4a3e] text-[12px] font-semibold"
              >
                {tick.label.whole}
              </text>
            )}
            {/* A fraction, stacked as MathText stacks one: top number, a
                line, bottom number. */}
            {tick.label && 'top' in tick.label && (
              <g
                data-fraction={`${tick.label.top}/${tick.label.bottom}`}
                className="fill-[#2d4a3e] text-[11px] font-semibold"
              >
                <text x={x} y={lineY + 23} textAnchor="middle">
                  {tick.label.top}
                </text>
                <line
                  x1={x - (labelWidth(tick.label) - 2) / 2}
                  y1={lineY + 27}
                  x2={x + (labelWidth(tick.label) - 2) / 2}
                  y2={lineY + 27}
                  stroke={INK}
                  strokeWidth="1.2"
                />
                <text x={x} y={lineY + 39} textAnchor="middle">
                  {tick.label.bottom}
                </text>
              </g>
            )}
          </g>
        );
      })}
      {thick && (
        <g data-inequality={line.ray ? `ray ${line.ray.arrow}` : 'segment'}>
          <line
            x1={thick.x1}
            y1={lineY}
            x2={thick.x2}
            y2={lineY}
            stroke={INK}
            strokeWidth={THICK}
          />
          {head && <polygon points={head} fill={INK} />}
        </g>
      )}
      {/* An open dot is a ring with the paper showing through, so the
          thick line stops at its edge; a filled dot is solid. */}
      {dots.map((dot) => (
        <circle
          key={dot.at}
          data-dot={dot.dot}
          cx={xFor(dot.at)}
          cy={lineY}
          r={dot.dot === 'open' ? DOT_RADIUS - RING_WIDTH / 2 : DOT_RADIUS}
          fill={dot.dot === 'open' ? '#ffffff' : INK}
          stroke={dot.dot === 'open' ? INK : 'none'}
          strokeWidth={dot.dot === 'open' ? RING_WIDTH : undefined}
        />
      ))}
      {marks.map((mark) => (
        <g key={mark.at} data-mark={mark.label}>
          <circle cx={xFor(mark.at)} cy={lineY} r="4" fill={INK} />
          <text
            x={xFor(mark.at)}
            y={lineY - 16}
            textAnchor="middle"
            className="fill-[#2d4a3e] text-[13px] font-bold"
          >
            {mark.label}
          </text>
        </g>
      ))}
      {/* The tapped tick: a filled dot with a ring round it, so it is told
          apart by shape and not by colour alone. */}
      {chosen !== null && chosen >= line.min && chosen <= line.max && (
        <g data-chosen="true">
          <circle cx={xFor(chosen)} cy={lineY} r="5.5" fill={CHOSEN} />
          <circle
            cx={xFor(chosen)}
            cy={lineY}
            r="10"
            fill="none"
            stroke={CHOSEN}
            strokeWidth="2.5"
          />
        </g>
      )}
    </svg>
  );

  // Arrow keys move along the line, like a slider; Tab leaves it. Only one
  // tick is in the Tab order: the chosen one, or the first.
  function handleKeyDown(event: KeyboardEvent, index: number) {
    const target =
      event.key === 'ArrowRight' || event.key === 'ArrowUp'
        ? index + 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowDown'
          ? index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? gaps
              : null;
    if (target === null) return;
    event.preventDefault();
    buttons.current[Math.min(gaps, Math.max(0, target))]?.focus();
  }
  const chosenTick = ticks.find((tick) => tick.at === chosen) ?? null;
  // Each tick's button is as wide as the gap between ticks and no wider, so
  // two ticks never share a place to tap.
  const reach = span / gaps / 2;

  return (
    <div className="max-w-full overflow-x-auto">
      <div
        role={onChoose ? 'group' : undefined}
        aria-label={onChoose ? description : undefined}
        className="relative w-full"
        style={{
          minWidth: `${String(Math.round(width * SMALLEST))}px`,
          maxWidth: `${String(Math.round(width * LARGEST))}px`,
        }}
      >
        {drawing}
        {onChoose &&
          ticks.map((tick) => {
            const x = xFor(tick.at);
            const left = Math.max(0, x - reach);
            const right = Math.min(width, x + reach);
            // What a sighted child sees: the printed number, or the
            // tick's place from the left, never its hidden value.
            const name = numberLineTickSpoken(line, tick.at);
            return (
              <button
                key={tick.index}
                ref={(node) => {
                  buttons.current[tick.index] = node;
                }}
                type="button"
                aria-label={
                  tick.mark
                    ? `${numberLineMarkSpoken(tick.mark.label).replace('Point ', '')}, ${name}`
                    : name
                }
                aria-pressed={tick.at === chosen}
                tabIndex={tick.index === (chosenTick?.index ?? 0) ? 0 : -1}
                disabled={disabled}
                className="group absolute top-0 h-full min-h-11 cursor-pointer outline-none hover:bg-forest-600/10 disabled:cursor-not-allowed"
                style={{
                  left: `${String((left / width) * 100)}%`,
                  width: `${String(((right - left) / width) * 100)}%`,
                }}
                onClick={() => {
                  onChoose(tick.at === chosen ? null : tick.at);
                }}
                onKeyDown={(event) => {
                  handleKeyDown(event, tick.index);
                }}
              >
                {/* Keyboard focus: a frame round the tick and its number.
                    Wider than a narrow button, so it is always easy to see. */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0.5 left-1/2 hidden w-full min-w-5 -translate-x-1/2 rounded-md border-2 border-forest-600 group-focus-visible:block"
                />
              </button>
            );
          })}
      </div>
    </div>
  );
}
